import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/soul";
import { Shuffle } from "lucide-react";

const TYPES = [
  { key: "all", label: "All" },
  { key: "well", label: "The Well" },
  { key: "word", label: "Word of the Day" },
  { key: "red-letter", label: "Red Letters" },
  { key: "teaching", label: "Ethiopian Canon" },
  { key: "quote", label: "Wisdom" },
  { key: "prompt", label: "Prompts" },
];

const ACCENT = {
  "red-letter": "#C1272D",
  teaching: "#D4AF37",
  quote: "#F5F1E7",
  prompt: "#A8A29E",
  word: "#D4AF37",
  well: "#E6B450",
};

const LABEL = {
  "red-letter": "Red Letter",
  teaching: "Ethiopian Canon",
  quote: "Wisdom",
  prompt: "Sit with this",
  word: "Word of the Day",
  well: "From The Well",
};

export default function DailyFeed() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/feed").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shuffle = () => setItems((prev) => [...prev].sort(() => Math.random() - 0.5));

  const shown = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <main data-testid="feed-page" className="max-w-7xl mx-auto px-6 py-16">
      <div className="max-w-3xl mb-10 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4">The Revolving Door</p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
          A daily feed of <span className="italic-serif text-[#D4AF37]">living words</span>
        </h1>
        <p className="mt-5 text-[#A8A29E] leading-relaxed text-lg">
          Red letters, the Ethiopian canon, timeless wisdom, and questions to sit with — rotating
          so a new truth meets you each time you return.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        {TYPES.map((t) => (
          <button
            key={t.key}
            data-testid={`feed-filter-${t.key}`}
            onClick={() => setFilter(t.key)}
            className={`text-sm px-4 py-1.5 rounded-full border transition-colors duration-300 ${
              filter === t.key
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                : "border-white/10 text-[#A8A29E] hover:border-[#D4AF37]/40"
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          data-testid="feed-shuffle-btn"
          onClick={shuffle}
          className="ml-auto inline-flex items-center gap-2 text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
        >
          <Shuffle size={16} /> Shuffle
        </button>
      </div>

      <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]" data-testid="feed-items">
        {shown.map((item, i) => (
          <div
            key={i}
            data-testid="feed-card"
            className="glass rounded-2xl mb-6 break-inside-avoid border-l-2 overflow-hidden"
            style={{ borderLeftColor: ACCENT[item.type] || "#D4AF37" }}
          >
            {item.type === "well" && item.image_url && (
              <img src={item.image_url} alt={item.text} loading="lazy" className="w-full h-auto block" />
            )}
            <div className="p-6">
              <p className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: ACCENT[item.type] }}>
                {LABEL[item.type]}
              </p>
              <p
                className="italic-serif text-xl leading-snug"
                style={{ color: item.type === "red-letter" ? "#C1272D" : "#F5F1E7" }}
              >
                “{item.text}”
              </p>
              <p className="mt-3 text-sm text-[#D4AF37]">
                — {item.author}
                {item.source ? <span className="text-[#A8A29E]"> · {item.source}</span> : null}
              </p>
              {item.reflection && (
                <p className="mt-3 text-sm text-[#A8A29E] leading-relaxed">{item.reflection}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
